/**
 * Utilitários para formatação e manipulação de dados do clima
 * Desenvolvido seguindo as melhores práticas de um desenvolvedor sênior
 */

import type {
  WeatherData,
  FormattedWeatherData,
  WeatherIconMapping,
  DailyForecast,
  HourlyData,
  DayPeriodInfo
} from '@/types/weather/weatherTypes'

// Mapeamento completo de condições climáticas para ícones Weather Icons (Iconify)
// Baseado no weather_conditions.json fornecido
export const weatherIconMapping: WeatherIconMapping = {
  // 1000 - Sunny/Clear
  1000: {
    day: 'wi:day-sunny',
    night: 'wi:night-clear',
    color: '#FFA726',
    gradient: ['#FFD54F', '#FFA726']
  },

  // 1003 - Partly cloudy
  1003: {
    day: 'wi:day-cloudy',
    night: 'wi:night-alt-cloudy',
    color: '#FFB74D',
    gradient: ['#FFCC02', '#FF8F00']
  },

  // 1006 - Cloudy
  1006: {
    day: 'wi:cloud',
    night: 'wi:cloud',
    color: '#78909C',
    gradient: ['#90A4AE', '#78909C']
  },

  // 1009 - Overcast
  1009: {
    day: 'wi:cloudy',
    night: 'wi:cloudy',
    color: '#546E7A',
    gradient: ['#78909C', '#546E7A']
  },

  // 1030 - Mist
  1030: {
    day: 'wi:day-haze',
    night: 'wi:night-fog',
    color: '#B0BEC5',
    gradient: ['#CFD8DC', '#B0BEC5']
  },

  // 1063 - Patchy rain possible
  1063: {
    day: 'wi:day-showers',
    night: 'wi:night-alt-showers',
    color: '#42A5F5',
    gradient: ['#64B5F6', '#42A5F5']
  },

  // 1066 - Patchy snow possible
  1066: {
    day: 'wi:day-snow',
    night: 'wi:night-alt-snow',
    color: '#E3F2FD',
    gradient: ['#F1F8E9', '#E3F2FD']
  },

  // 1069 - Patchy sleet possible
  1069: {
    day: 'wi:day-sleet',
    night: 'wi:night-alt-sleet',
    color: '#90A4AE',
    gradient: ['#B0BEC5', '#90A4AE']
  },

  // 1072 - Patchy freezing drizzle possible
  1072: {
    day: 'wi:day-sprinkle',
    night: 'wi:night-alt-sprinkle',
    color: '#42A5F5',
    gradient: ['#64B5F6', '#42A5F5']
  },

  // 1087 - Thundery outbreaks possible
  1087: {
    day: 'wi:day-thunderstorm',
    night: 'wi:night-alt-thunderstorm',
    color: '#512DA8',
    gradient: ['#673AB7', '#512DA8']
  },

  // 1114 - Blowing snow
  1114: {
    day: 'wi:snow-wind',
    night: 'wi:snow-wind',
    color: '#E3F2FD',
    gradient: ['#F1F8E9', '#E3F2FD']
  },

  // 1117 - Blizzard
  1117: {
    day: 'wi:snow-wind',
    night: 'wi:snow-wind',
    color: '#E3F2FD',
    gradient: ['#F1F8E9', '#E3F2FD']
  },

  // 1135 - Fog
  1135: {
    day: 'wi:day-fog',
    night: 'wi:night-fog',
    color: '#B0BEC5',
    gradient: ['#CFD8DC', '#B0BEC5']
  },

  // 1147 - Freezing fog
  1147: {
    day: 'wi:day-fog',
    night: 'wi:night-fog',
    color: '#B0BEC5',
    gradient: ['#CFD8DC', '#B0BEC5']
  },

  // 1150 - Patchy light drizzle
  1150: {
    day: 'wi:day-sprinkle',
    night: 'wi:night-alt-sprinkle',
    color: '#42A5F5',
    gradient: ['#64B5F6', '#42A5F5']
  },

  // 1153 - Light drizzle
  1153: {
    day: 'wi:day-sprinkle',
    night: 'wi:night-alt-sprinkle',
    color: '#42A5F5',
    gradient: ['#64B5F6', '#42A5F5']
  },

  // 1168 - Freezing drizzle
  1168: {
    day: 'wi:day-sprinkle',
    night: 'wi:night-alt-sprinkle',
    color: '#42A5F5',
    gradient: ['#64B5F6', '#42A5F5']
  },

  // 1171 - Heavy freezing drizzle
  1171: {
    day: 'wi:day-sprinkle',
    night: 'wi:night-alt-sprinkle',
    color: '#1E88E5',
    gradient: ['#42A5F5', '#1E88E5']
  },

  // 1180 - Patchy light rain
  1180: {
    day: 'wi:day-showers',
    night: 'wi:night-alt-showers',
    color: '#42A5F5',
    gradient: ['#64B5F6', '#42A5F5']
  },

  // 1183 - Light rain
  1183: {
    day: 'wi:day-showers',
    night: 'wi:night-alt-showers',
    color: '#42A5F5',
    gradient: ['#64B5F6', '#42A5F5']
  },

  // 1186 - Moderate rain at times
  1186: {
    day: 'wi:day-rain',
    night: 'wi:night-alt-rain',
    color: '#1E88E5',
    gradient: ['#42A5F5', '#1E88E5']
  },

  // 1189 - Moderate rain
  1189: {
    day: 'wi:day-rain',
    night: 'wi:night-alt-rain',
    color: '#1E88E5',
    gradient: ['#42A5F5', '#1E88E5']
  },

  // 1192 - Heavy rain at times
  1192: {
    day: 'wi:day-rain',
    night: 'wi:night-alt-rain',
    color: '#1565C0',
    gradient: ['#1E88E5', '#1565C0']
  },

  // 1195 - Heavy rain
  1195: {
    day: 'wi:day-rain',
    night: 'wi:night-alt-rain',
    color: '#1565C0',
    gradient: ['#1E88E5', '#1565C0']
  },

  // 1198 - Light freezing rain
  1198: {
    day: 'wi:day-rain',
    night: 'wi:night-alt-rain',
    color: '#42A5F5',
    gradient: ['#64B5F6', '#42A5F5']
  },

  // 1201 - Moderate or heavy freezing rain
  1201: {
    day: 'wi:day-rain',
    night: 'wi:night-alt-rain',
    color: '#1565C0',
    gradient: ['#1E88E5', '#1565C0']
  },

  // 1204 - Light sleet
  1204: {
    day: 'wi:day-sleet',
    night: 'wi:night-alt-sleet',
    color: '#90A4AE',
    gradient: ['#B0BEC5', '#90A4AE']
  },

  // 1207 - Moderate or heavy sleet
  1207: {
    day: 'wi:day-sleet',
    night: 'wi:night-alt-sleet',
    color: '#78909C',
    gradient: ['#90A4AE', '#78909C']
  },

  // 1210 - Patchy light snow
  1210: {
    day: 'wi:day-snow',
    night: 'wi:night-alt-snow',
    color: '#E3F2FD',
    gradient: ['#F1F8E9', '#E3F2FD']
  },

  // 1213 - Light snow
  1213: {
    day: 'wi:day-snow',
    night: 'wi:night-alt-snow',
    color: '#E3F2FD',
    gradient: ['#F1F8E9', '#E3F2FD']
  },

  // 1216 - Patchy moderate snow
  1216: {
    day: 'wi:day-snow',
    night: 'wi:night-alt-snow',
    color: '#E3F2FD',
    gradient: ['#F1F8E9', '#E3F2FD']
  },

  // 1219 - Moderate snow
  1219: {
    day: 'wi:day-snow',
    night: 'wi:night-alt-snow',
    color: '#E3F2FD',
    gradient: ['#F1F8E9', '#E3F2FD']
  },

  // 1222 - Patchy heavy snow
  1222: {
    day: 'wi:day-snow',
    night: 'wi:night-alt-snow',
    color: '#E3F2FD',
    gradient: ['#F1F8E9', '#E3F2FD']
  },

  // 1225 - Heavy snow
  1225: {
    day: 'wi:day-snow',
    night: 'wi:night-alt-snow',
    color: '#E3F2FD',
    gradient: ['#F1F8E9', '#E3F2FD']
  },

  // 1237 - Ice pellets
  1237: {
    day: 'wi:day-hail',
    night: 'wi:night-alt-hail',
    color: '#90A4AE',
    gradient: ['#B0BEC5', '#90A4AE']
  },

  // 1240 - Light rain shower
  1240: {
    day: 'wi:day-showers',
    night: 'wi:night-alt-showers',
    color: '#42A5F5',
    gradient: ['#64B5F6', '#42A5F5']
  },

  // 1243 - Moderate or heavy rain shower
  1243: {
    day: 'wi:day-rain',
    night: 'wi:night-alt-rain',
    color: '#1565C0',
    gradient: ['#1E88E5', '#1565C0']
  },

  // 1246 - Torrential rain shower
  1246: {
    day: 'wi:day-rain',
    night: 'wi:night-alt-rain',
    color: '#0D47A1',
    gradient: ['#1565C0', '#0D47A1']
  },

  // 1249 - Light sleet showers
  1249: {
    day: 'wi:day-sleet',
    night: 'wi:night-alt-sleet',
    color: '#90A4AE',
    gradient: ['#B0BEC5', '#90A4AE']
  },

  // 1252 - Moderate or heavy sleet showers
  1252: {
    day: 'wi:day-sleet',
    night: 'wi:night-alt-sleet',
    color: '#78909C',
    gradient: ['#90A4AE', '#78909C']
  },

  // 1255 - Light snow showers
  1255: {
    day: 'wi:day-snow',
    night: 'wi:night-alt-snow',
    color: '#E3F2FD',
    gradient: ['#F1F8E9', '#E3F2FD']
  },

  // 1258 - Moderate or heavy snow showers
  1258: {
    day: 'wi:day-snow',
    night: 'wi:night-alt-snow',
    color: '#E3F2FD',
    gradient: ['#F1F8E9', '#E3F2FD']
  },

  // 1261 - Light showers of ice pellets
  1261: {
    day: 'wi:day-hail',
    night: 'wi:night-alt-hail',
    color: '#90A4AE',
    gradient: ['#B0BEC5', '#90A4AE']
  },

  // 1264 - Moderate or heavy showers of ice pellets
  1264: {
    day: 'wi:day-hail',
    night: 'wi:night-alt-hail',
    color: '#78909C',
    gradient: ['#90A4AE', '#78909C']
  },

  // 1273 - Patchy light rain with thunder
  1273: {
    day: 'wi:day-thunderstorm',
    night: 'wi:night-alt-thunderstorm',
    color: '#512DA8',
    gradient: ['#673AB7', '#512DA8']
  },

  // 1276 - Moderate or heavy rain with thunder
  1276: {
    day: 'wi:day-thunderstorm',
    night: 'wi:night-alt-thunderstorm',
    color: '#4A148C',
    gradient: ['#512DA8', '#4A148C']
  },

  // 1279 - Patchy light snow with thunder
  1279: {
    day: 'wi:day-thunderstorm',
    night: 'wi:night-alt-thunderstorm',
    color: '#512DA8',
    gradient: ['#673AB7', '#512DA8']
  },

  // 1282 - Moderate or heavy snow with thunder
  1282: {
    day: 'wi:day-thunderstorm',
    night: 'wi:night-alt-thunderstorm',
    color: '#4A148C',
    gradient: ['#512DA8', '#4A148C']
  }
}

/**
 * Obter ícone do clima baseado no código da condição e período do dia
 */
const getWeatherIcon = (conditionCode: number, isDay?: boolean): { icon: string; color: string; gradient?: string[] } => {
  const iconData = weatherIconMapping[conditionCode]

  if (!iconData) {
    return {
      icon: 'wi:na',
      color: '#9E9E9E',
      gradient: ['#BDBDBD', '#9E9E9E']
    }
  }

  const icon = isDay !== false ? iconData.day : iconData.night

  return {
    icon,
    color: iconData.color,
    gradient: iconData.gradient
  }
}

/**
 * Formatar temperatura com unidade baseada no sistema
 */
export const formatTemperature = (temp: number, unitSystem: 'metric' | 'imperial' = 'metric', showUnit = true): string => {
  const rounded = Math.round(temp)

  if (!showUnit) return `${rounded}`

  return unitSystem === 'imperial' ? `${rounded}°F` : `${rounded}°C`
}

/**
 * Formatar velocidade do vento baseada no sistema
 */
export const formatWindSpeed = (speed: number, unitSystem: 'metric' | 'imperial' = 'metric'): string => {
  const rounded = Math.round(speed)

  return unitSystem === 'imperial' ? `${rounded} mph` : `${rounded} km/h`
}

/**
 * Formatar porcentagem
 */
export const formatPercentage = (value: number): string => {
  return `${Math.round(value)}%`
}

/**
 * Formatar pressão atmosférica baseada no sistema
 */
export const formatPressure = (pressure: number, unitSystem: 'metric' | 'imperial' = 'metric'): string => {
  const rounded = Math.round(pressure)

  return unitSystem === 'imperial' ? `${rounded} inHg` : `${rounded} hPa`
}

/**
 * Formatar visibilidade baseada no sistema
 */
export const formatVisibility = (visibility: number, unitSystem: 'metric' | 'imperial' = 'metric'): string => {
  const rounded = Math.round(visibility)

  return unitSystem === 'imperial' ? `${rounded} mi` : `${rounded} km`
}

/**
 * Determinar o período do dia baseado na hora
 */
export const getCurrentPeriod = (): string => {
  const hour = new Date().getHours()

  if (hour >= 6 && hour < 12) return 'morning'
  if (hour >= 12 && hour < 18) return 'afternoon'
  if (hour >= 18 && hour < 22) return 'evening'

  return 'night'
}

/**
 * Determinar se é dia ou noite baseado na hora
 */
export const isDayTime = (dateTime?: string): boolean => {
  const date = dateTime ? new Date(dateTime) : new Date()
  const hour = date.getHours()

  return hour >= 6 && hour < 18
}

/**
 * Obter informações do período do dia
 */
export const getDayPeriodInfo = (dateTime?: string): DayPeriodInfo => {
  const date = dateTime ? new Date(dateTime) : new Date()
  const hour = date.getHours()
  const isDay = hour >= 6 && hour < 18

  let period: any = 'night'

  if (hour >= 6 && hour < 12) period = 'morning'
  else if (hour >= 12 && hour < 18) period = 'afternoon'
  else if (hour >= 18 && hour < 22) period = 'evening'

  return {
    isDay,
    period,
    hour
  }
}

/**
 * Formatar timestamp para hora legível
 */
export const formatTime = (timestamp: number): string => {
  return new Date(timestamp * 1000).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit'
  })
}

/**
 * Formatar string de data/hora para hora legível
 */
export const formatTimeString = (timeString: string): string => {
  const date = new Date(timeString)

  return date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit'
  })
}

/**
 * Formatar data para exibição
 */
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  const today = new Date()
  const tomorrow = new Date(today)

  tomorrow.setDate(tomorrow.getDate() + 1)

  if (date.toDateString() === today.toDateString()) {
    return 'Hoje'
  } else if (date.toDateString() === tomorrow.toDateString()) {
    return 'Amanhã'
  } else {
    return date.toLocaleDateString('pt-BR', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit'
    })
  }
}

/**
 * Formatar data para dia da semana
 * @param dateString - String da data no formato YYYY-MM-DD
 * @param language - Idioma para formatação ('pt' ou 'en')
 * @returns Nome do dia da semana no idioma especificado
 */
export const getDayOfWeek = (dateString: string, language: 'pt' | 'en' = 'pt'): string => {
  // Criar data local para evitar problemas de timezone
  const [year, month, day] = dateString.split('-').map(Number)
  const date = new Date(year, month - 1, day) // month é 0-indexed

  const locale = language === 'en' ? 'en-US' : 'pt-BR'

  return date.toLocaleDateString(locale, {
    weekday: 'long'
  })
}

/**
 * Obter nome do dia da semana
 */
export const getDayName = (dateString: string): string => {
  const date = new Date(dateString)

  return date.toLocaleDateString('pt-BR', { weekday: 'long' })
}

/**
 * Determinar cor do índice UV
 */
export const getUVIndexColor = (uvIndex: number): string => {
  if (uvIndex <= 2) return '#4CAF50' // Verde
  if (uvIndex <= 5) return '#FFEB3B' // Amarelo
  if (uvIndex <= 7) return '#FF9800' // Laranja
  if (uvIndex <= 10) return '#F44336' // Vermelho

  return '#9C27B0' // Roxo (extremo)
}

/**
 * Determinar cor da qualidade do ar baseada na visibilidade
 */
export const getAirQualityColor = (visibility: number): string => {
  if (visibility >= 10) return '#4CAF50' // Excelente
  if (visibility >= 7) return '#8BC34A'  // Bom
  if (visibility >= 4) return '#FFEB3B'  // Moderado
  if (visibility >= 2) return '#FF9800'  // Ruim

  return '#F44336' // Péssimo
}

/**
 * Filtrar dados horários futuros
 */
export const filterFutureHourly = (hourlyData: HourlyData[]): HourlyData[] => {
  const now = new Date()

  return hourlyData.filter(hour => {
    const hourTime = new Date(hour.time)

    return hourTime > now
  })
}

/**
 * Formatar qualidade do ar
 */
export const formatAirQuality = (airQuality: { us_epa_index: number }): string => {
  const index = airQuality.us_epa_index

  if (index === 1) return 'Boa'
  if (index === 2) return 'Moderada'
  if (index === 3) return 'Insalubre para grupos sensíveis'
  if (index === 4) return 'Insalubre'
  if (index === 5) return 'Muito insalubre'
  if (index === 6) return 'Perigosa'

  return 'Desconhecida'
}

/**
 * Formatar índice UV
 */
export const formatUVIndex = (uvIndex: number): string => {
  return uvIndex.toFixed(1)
}

/**
 * Obter cor baseada na temperatura
 */
export const getTemperatureColor = (temperature: number, isDark: boolean = false): string => {
  // Temperaturas muito baixas (< 10°C) - Azul escuro
  if (temperature < 10) {
    return isDark ? '#64B5F6' : '#1976D2'
  }

  // Temperaturas baixas (10-20°C) - Azul claro
  if (temperature < 20) {
    return isDark ? '#81C784' : '#388E3C'
  }

  // Temperaturas médias (20-30°C) - Amarelo/Laranja
  if (temperature < 30) {
    return isDark ? '#FFB74D' : '#F57C00'
  }

  // Temperaturas altas (≥30°C) - Vermelho claro
  return isDark ? '#F48FB1' : '#D32F2F'
}

/**
 * Formatar direção do vento
 */
export const formatWindDirection = (direction: string, degree: number): string => {
  return `${direction} (${degree}°)`
}

/**
 * Formatar precipitação baseada no sistema
 */
export const formatPrecipitation = (precipitation: number, unitSystem: 'metric' | 'imperial' = 'metric'): string => {
  const value = precipitation.toFixed(1)

  return unitSystem === 'imperial' ? `${value} in` : `${value} mm`
}

/**
 * Formatar radiação solar
 */
export const formatSolarRadiation = (solarRadiation: { shortWave: number }): string => {
  return `${Math.round(solarRadiation.shortWave)} W/m²`
}

/**
 * Formatar dados do clima para exibição no componente
 */
export const formatWeatherData = (data: WeatherData): FormattedWeatherData => {
  const currentPeriodInfo = getDayPeriodInfo(data.location.localtime)
  const currentIcon = getWeatherIcon(data.current.condition.code, currentPeriodInfo.isDay)

  // Pegar dados horários do primeiro dia (hoje) se existir
  const todayForecast = data.forecast[0]
  const futureHours = todayForecast ? filterFutureHourly(todayForecast.hourlyData) : []

  return {
    current: {
      temperature: formatTemperature(data.current.temperature),
      condition: data.current.condition.text,
      icon: currentIcon.icon,
      feelTemperature: formatTemperature(data.current.feelsLike),
      windSpeed: formatWindSpeed(data.current.windSpeed),
      pressure: formatPressure(data.current.pressure),
      airQuality: formatAirQuality(data.current.airQuality),
      solarRadiation: formatSolarRadiation(data.current.solarRadiation)
    },
    hourly: futureHours.slice(0, 8).map((hour: HourlyData) => {
      const hourPeriodInfo = getDayPeriodInfo(hour.time)
      const hourIcon = getWeatherIcon(hour.condition.code, hourPeriodInfo.isDay)

      return {
        time: formatTimeString(hour.time),
        temperature: formatTemperature(hour.temperature),
        icon: hourIcon.icon,
        condition: hour.condition.text,
        chanceOfRain: formatPercentage(hour.chanceOfRain)
      }
    }),
    forecast: data.forecast.slice(0, 3).map((day: DailyForecast) => {
      const dayIcon = getWeatherIcon(day.dayData.condition.code, true) // Usar ícone do dia

      return {
        day: formatDate(day.date),
        date: day.date,
        tempMax: formatTemperature(day.dayData.temperature.max),
        tempMin: formatTemperature(day.dayData.temperature.min),
        icon: dayIcon.icon,
        condition: day.dayData.condition.text,
        sunrise: day.dayData.astro.sunrise,
        sunset: day.dayData.astro.sunset,
        moonPhase: day.dayData.astro.moon_phase
      }
    })
  }
}

/**
 * Determinar se deve mostrar alerta de temperatura
 */
export const shouldShowTemperatureAlert = (temperature: number): boolean => {
  return temperature > 35 || temperature < 5
}

/**
 * Determinar se deve mostrar alerta de UV
 */
export const shouldShowUVAlert = (uvIndex?: number): boolean => {
  return uvIndex ? uvIndex > 7 : false
}

/**
 * Determinar se deve mostrar alerta de vento
 */
export const shouldShowWindAlert = (windSpeed: number): boolean => {
  return windSpeed > 30
}

/**
 * Obter gradiente baseado no período do dia
 */
export const getDayPeriodGradient = (period: string): string[] => {
  const gradients = {
    morning: ['#FFE082', '#FFA726'],
    afternoon: ['#FFD54F', '#FF8F00'],
    evening: ['#FF8A65', '#FF5722'],
    night: ['#7986CB', '#3F51B5']
  }

  return gradients[period as keyof typeof gradients] || gradients.afternoon
}

/**
 * Validar se os dados do clima são válidos
 */
export const isValidWeatherData = (data: WeatherData | null): boolean => {
  if (!data) return false

  return Boolean(
    data.current &&
    data.location &&
    data.forecast &&
    Array.isArray(data.forecast) &&
    data.forecast.length > 0
  )
}

/**
 * Obter cor baseada na qualidade do ar
 */
export const getAirQualityColorByIndex = (index: number): string => {
  if (index === 1) return '#4CAF50' // Boa
  if (index === 2) return '#8BC34A' // Moderada
  if (index === 3) return '#FFEB3B' // Insalubre para grupos sensíveis
  if (index === 4) return '#FF9800' // Insalubre
  if (index === 5) return '#F44336' // Muito insalubre
  if (index === 6) return '#9C27B0' // Perigosa

  return '#9E9E9E' // Desconhecida
}

// Exportar objeto utilitário como default
const weatherUtilsDefault = {
  // Formatação
  formatTemperature,
  formatWindSpeed,
  formatPercentage,
  formatPressure,
  formatVisibility,
  formatTime,
  formatTimeString,
  formatDate,
  getDayOfWeek,
  formatWeatherData,
  formatAirQuality,
  formatSolarRadiation,
  formatUVIndex,
  formatWindDirection,
  formatPrecipitation,

  // Períodos e tempo
  getCurrentPeriod,
  getDayName,
  isDayTime,
  getDayPeriodInfo,
  filterFutureHourly,

  // Cores e indicadores
  getUVIndexColor,
  getAirQualityColor,
  getAirQualityColorByIndex,
  getDayPeriodGradient,
  getTemperatureColor,

  // Alertas
  shouldShowTemperatureAlert,
  shouldShowUVAlert,
  shouldShowWindAlert,

  // Validação
  isValidWeatherData,

  // Ícones
  weatherIconMapping,
  getWeatherIcon: (conditionCode: number, isDay?: boolean) => getWeatherIcon(conditionCode, isDay)
}

export default weatherUtilsDefault
